import React from 'react';
import { Button, Slide, TextField, Divider, Paper } from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import './EmployeeTicketsTable.css';

class EmployeeTicketsTable extends React.Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.state = {
      search: '',
      sortConfig: { key: null, direction: 'asc' },
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,
      isSticky: false,
    };
  }

  componentDidMount() {
    const navbar = document.querySelector('.top-nav');
    const sentinel = document.createElement('div');
    navbar.parentNode.insertBefore(sentinel, navbar);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.setState({ isSticky: !entry.isIntersecting });
      },
      { threshold: [1] }
    );
    this.observer.observe(sentinel);
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  handleSearchChange = (e) => this.setState({ search: e.target.value });

  handleSort = (key) => {
    const { sortConfig } = this.state;
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc')
      direction = 'desc';
    this.setState({ sortConfig: { key, direction } });
  };

  parseTicketTime = (time) => {
    if (!time) return 0;
    try {
      const [t, meridian] = time
        .toUpperCase()
        .replace('A.M.', 'AM')
        .replace('P.M.', 'PM')
        .split(' ');
      if (!t || !meridian) return 0;
      let [hours, minutes] = t.split(':').map(Number);
      if (meridian === 'PM' && hours < 12) hours += 12;
      if (meridian === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    } catch {
      return 0;
    }
  };

  getDayOfWeekNumber = (date) => {
    if (!date) return 0;
    const d = new Date(date);
    if (isNaN(d)) return 0;
    const dayName = d.toLocaleDateString('en-US', {
      weekday: 'long',
      timeZone: 'America/Chicago',
    });
    const days = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 7,
    };
    return days[dayName] || 0;
  };

  getDayName = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-US', {
          weekday: 'long',
          timeZone: 'America/Chicago',
        })
      : '';

  sortTicketsBackendStyle = (tickets) => {
    return tickets.slice().sort((a, b) => {
      const dayA = this.getDayOfWeekNumber(a.createdDate);
      const dayB = this.getDayOfWeekNumber(b.createdDate);
      if (dayA !== dayB) return dayA - dayB;
      return (
        this.parseTicketTime(a.ticketTime) - this.parseTicketTime(b.ticketTime)
      );
    });
  };

  getFilteredTickets = (tickets) => {
    const { search, sortConfig } = this.state;
    let filtered = tickets;

    if (search) {
      filtered = filtered.filter((t) =>
        t.employee?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (sortConfig.key) {
      filtered = filtered.slice().sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'ticketAmount') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (sortConfig.key === 'ticketTime') {
          aValue = this.parseTicketTime(aValue);
          bValue = this.parseTicketTime(bValue);
        }

        if (sortConfig.key === 'employee') {
          aValue = aValue?.toLowerCase() || '';
          bValue = bValue?.toLowerCase() || '';
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  calculateTotalAmount = (tickets) => {
    return tickets.reduce(
      (sum, t) => sum + (parseFloat(t.ticketAmount) || 0),
      0
    );
  };

  render() {
    const { sortConfig, isSticky, isMobile, search } = this.state;
    const {
      showEditEmployeeTickets,
      onClose,
      userInfo,
      filteredEmployeeTickets,
    } = this.props;

    // Group by machine
    const machinesMap = {};
    filteredEmployeeTickets.forEach((ticket) => {
      const machine = ticket.machine || 'Unknown';
      if (!machinesMap[machine]) machinesMap[machine] = [];
      machinesMap[machine].push(ticket);
    });

    const hasCompanyIds = userInfo?.companyIds?.length > 0;

    return (
      <div style={{ overflow: 'hidden', height: '100vh' }}>
        <div
          className="notesSidebarHolder"
          style={{
            width: showEditEmployeeTickets ? '25%' : '0',
            transition: 'width 0.4s ease',
            position: 'fixed',
            right: 0,
            top: hasCompanyIds
              ? isMobile
                ? isSticky
                  ? '131px'
                  : '186px'
                : isSticky
                ? '139px'
                : '194px'
              : isMobile
              ? '132px'
              : isSticky
              ? '75px'
              : '140px',
            height: isSticky ? 'calc(100% - 75px)' : 'calc(100% - 130px)',
            backgroundColor: '#f9f9f9',
            boxShadow: showEditEmployeeTickets
              ? '-4px 0 12px rgba(0,0,0,0.1)'
              : 'none',
            zIndex: 1200,
          }}
        >
          <Slide
            in={showEditEmployeeTickets}
            direction="left"
            mountOnEnter
            unmountOnExit
          >
            <div style={{ padding: '16px' }}>
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <h4 style={{ margin: 0 }}>Employee Tickets</h4>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  color="secondary"
                  startIcon={<CloseIcon />}
                >
                  Close
                </Button>
              </div>

              {/* Search */}
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Search employee..."
                value={search}
                onChange={this.handleSearchChange}
                style={{ marginBottom: '16px' }}
              />

              <Divider />

              {/* Machine Tables */}
              <Paper
                style={{
                  marginTop: '16px',
                  maxHeight: '63vh',
                  overflowY: 'auto',
                }}
              >
                {Object.keys(machinesMap).map((machine) => {
                  const ticketsSorted = this.sortTicketsBackendStyle(
                    machinesMap[machine]
                  );
                  const tickets = this.getFilteredTickets(ticketsSorted);
                  let lastDay = null;

                  return (
                    <div style={{ marginBottom: 30 }}>
                      {/* Table Header */}
                      <div
                        style={{
                          display: 'flex',
                          fontWeight: 'bold',
                          padding: '8px 12px',
                          background: '#e0e0e0',
                        }}
                      >
                        <div
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => this.handleSort('createdDate')}
                        >
                          Day{' '}
                          {sortConfig.key === 'createdDate'
                            ? sortConfig.direction === 'asc'
                              ? '↑'
                              : '↓'
                            : ''}
                        </div>
                        <div
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => this.handleSort('ticketAmount')}
                        >
                          Amount{' '}
                          {sortConfig.key === 'ticketAmount'
                            ? sortConfig.direction === 'asc'
                              ? '↑'
                              : '↓'
                            : ''}
                        </div>
                        <div
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => this.handleSort('ticketTime')}
                        >
                          Time{' '}
                          {sortConfig.key === 'ticketTime'
                            ? sortConfig.direction === 'asc'
                              ? '↑'
                              : '↓'
                            : ''}
                        </div>
                        <div
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => this.handleSort('employee')}
                        >
                          Employee{' '}
                          {sortConfig.key === 'employee'
                            ? sortConfig.direction === 'asc'
                              ? '↑'
                              : '↓'
                            : ''}
                        </div>
                      </div>

                      {/* Rows */}
                      {tickets.map((t, idx) => {
                        const dayName = this.getDayName(t.createdDate);
                        const showDay = dayName !== lastDay;
                        lastDay = dayName;

                        return (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              padding: '8px 12px',
                              borderBottom: '1px solid #eee',
                              background: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                fontWeight: showDay ? 'bold' : 'normal',
                              }}
                            >
                              {showDay ? dayName : ''}
                            </div>
                            <div style={{ flex: 1 }}>
                              ${parseFloat(t.ticketAmount)}
                            </div>
                            <div style={{ flex: 1 }}>{t.ticketTime}</div>
                            <div style={{ flex: 1 }}>{t.employee}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </Paper>

              {/* Total */}
              <div
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginTop: '16px',
                  color: '#444',
                }}
              >
                Tickets ({filteredEmployeeTickets.length}) → $
                {this.calculateTotalAmount(filteredEmployeeTickets)}
              </div>
            </div>
          </Slide>
        </div>
      </div>
    );
  }
}

export default EmployeeTicketsTable;
